import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { showApiError } from '@/lib/utils/notifications'
import { fetchEorpByDocumentId } from '@/lib/api/eorpService'
import eorpStyles from '@/components/modals/NewAgreementModal.module.css'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { DOCUMENT_EORP_MODAL_TITLE } from '../constants/documentEorp'
import type { DocumentEorpModalProps } from '../types'

/**
 * Angular `EorpDocumentPopupComponent` — document-scoped EORP opened from
 * service-agreement "Click here…" links (`/explanation-service-aggrement/:id`).
 */
export function DocumentEorpModal({
  isOpen,
  documentId,
  initialHtml,
  onClose,
}: DocumentEorpModalProps) {
  const [section, setSection] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !documentId) return

    const seeded = (initialHtml ?? '').trim()
    if (seeded) {
      setSection(seeded)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setSection('')
    void fetchEorpByDocumentId(documentId)
      .then((res) => {
        if (cancelled) return
        setSection(res.eorp_section ?? '')
      })
      .catch((error) => {
        if (cancelled) return
        // Match Angular: `notify(error.error, 0)` — keep the API description on 404.
        showApiError(error)
        onClose()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, documentId, initialHtml, onClose])

  return (
    <OnScreenModal
      isOpen={isOpen && Boolean(documentId)}
      title={DOCUMENT_EORP_MODAL_TITLE}
      onClose={onClose}
      maxWidth="max-w-800"
      headerVariant="centeredTitle"
      bodyScrollOnly
      panelClassName={eorpStyles.eorpModalPanel}
      headerClassName={eorpStyles.eorpModalHeader}
      bodyClassName={eorpStyles.eorpModalBody}
      footerClassName={eorpStyles.eorpModalFooter}
      panelDataTestId="document-eorp-modal-panel"
      footer={
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className={cn(primaryEntityActionButtonClass, 'min-w-[100px]')}
            data-testid="document-eorp-modal-ok"
          >
            OK
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading" />
        </div>
      ) : section ? (
        <div
          className={eorpStyles.eorpSectionHtml}
          dangerouslySetInnerHTML={{ __html: section }}
          data-testid="document-eorp-content"
        />
      ) : (
        <p className="text-sm text-slate-500">No details available.</p>
      )}
    </OnScreenModal>
  )
}
